/**
 * Spreadsheet Viewer Service
 * Converts data to a format suitable for display in an interactive table
 */

export interface SpreadsheetColumn {
  key: string;
  header: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string;
}

export interface SpreadsheetData {
  title: string;
  columns: SpreadsheetColumn[];
  rows: any[];
  sheetName?: string;
}

/**
 * Format a date value
 */
export const formatDate = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    const date = new Date(value);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  return String(value);
};

/**
 * Format a currency value
 */
export const formatCurrency = (value: any): string => {
  if (!value) return '0';
  const num = parseFloat(String(value));
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a number value
 */
export const formatNumber = (value: any): string => {
  if (!value && value !== 0) return '';
  const num = parseFloat(String(value));
  return num.toLocaleString('en-IN');
};

/**
 * Calculate column widths based on content
 */
export const calculateColumnWidths = (
  columns: SpreadsheetColumn[],
  rows: any[]
): number[] => {
  return columns.map((col) => {
    // Start with header length
    let maxLength = col.header.length;

    // Check data lengths
    rows.forEach((row) => {
      const value = String(row[col.key] || '');
      if (value.length > maxLength) {
        maxLength = value.length;
      }
    });

    // Add padding and minimum width
    return Math.max(100, (maxLength + 2) * 8);
  });
};

/**
 * Export data for spreadsheet display
 */
export const prepareSpreadsheetData = (
  title: string,
  columns: SpreadsheetColumn[],
  rows: any[]
): SpreadsheetData => {
  return {
    title,
    columns,
    rows,
  };
};

/**
 * Get cell value with formatting
 */
export const getFormattedCellValue = (
  row: any,
  column: SpreadsheetColumn
): string => {
  const value = row[column.key];
  if (column.format) {
    return column.format(value);
  }
  return String(value || '');
};

/**
 * Get summary statistics for a column
 */
export const getColumnSummary = (rows: any[], columnKey: string) => {
  const values = rows
    .map((row) => parseFloat(String(row[columnKey])))
    .filter((val) => !isNaN(val));

  if (values.length === 0) return null;

  return {
    sum: values.reduce((a, b) => a + b, 0),
    average: values.reduce((a, b) => a + b, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
  };
};
